import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  Edit as EditIcon, DeleteOutline as DeleteIcon, FitnessCenter as ExerciseIcon,
  Timer as TimeIcon, Repeat as RepsIcon, Scale as WeightIcon, DirectionsRun as DistanceIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const PARAM_ICON = {
  TIME: <TimeIcon sx={{ fontSize: 15 }} />,
  REPS: <RepsIcon sx={{ fontSize: 15 }} />,
  WEIGHT: <WeightIcon sx={{ fontSize: 15 }} />,
  DISTANCE: <DistanceIcon sx={{ fontSize: 15 }} />
};

const boxedActionSx = (colorFn) => ({
  border: '1px solid', borderColor: 'hairline.strong', borderRadius: 1.5, color: colorFn,
  '&:hover': { backgroundColor: 'action.hover', borderColor: colorFn }
});

const fmt = (d) => {
  try { return format(parseISO(String(d).slice(0, 10)), 'dd/MM/yyyy', { locale: es }); } catch { return d; }
};

/**
 * Fila de rendimiento a lo ANCHO (estilo Mis sesiones): cliente · ejercicio · período · parámetros ·
 * series · acciones. Click en la fila abre el panel lateral de detalle.
 */
function PerformanceCard({ row, selected = false, onView, onEdit, onDelete }) {
  const { t } = useTranslation(['buttons']);
  const params = row.exercise?.parameters?.map((p) => p.parameter_type) || [];
  const samePeriod = String(row.period_start_date).slice(0, 10) === String(row.period_end_date).slice(0, 10);

  return (
    <Box
      onClick={onView}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onView(); } }}
      sx={(th) => ({
        cursor: 'pointer',
        backgroundColor: 'surface.raised',
        border: '1px solid',
        borderColor: selected ? 'gold.main' : 'hairline.main',
        borderLeft: '4px solid',
        borderLeftColor: 'gold.main',
        borderRadius: '14px',
        p: { xs: 1.5, sm: 2 },
        boxShadow: th.palette.shadow.card,
        transition: 'transform 0.2s cubic-bezier(0.32,0.72,0,1), background-color 0.2s, border-color 0.2s',
        '&:hover': { transform: 'translateY(-2px)', backgroundColor: 'surface.hover' }
      })}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1.25, sm: 2 }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        {/* Cliente */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: { sm: 180 } }}>
          <Avatar src={row.client?.photo} sx={{ width: 34, height: 34, bgcolor: 'gold.soft', color: 'gold.main', fontSize: '0.85rem' }}>
            {row.client?.first_name?.[0]}
          </Avatar>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
            {row.client?.first_name} {row.client?.last_name}
          </Typography>
        </Stack>

        {/* Ejercicio */}
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, flexGrow: 1 }}>
          <ExerciseIcon sx={{ fontSize: 18, color: 'gold.main', flexShrink: 0 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>{row.exercise?.name}</Typography>
        </Stack>

        {/* Período */}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.secondary', display: { xs: 'none', md: 'flex' }, minWidth: 150 }}>
          <CalendarIcon sx={{ fontSize: 15 }} />
          <Typography variant="caption" sx={{ fontWeight: 600 }} noWrap>
            {samePeriod ? fmt(row.period_start_date) : `${fmt(row.period_start_date)} – ${fmt(row.period_end_date)}`}
          </Typography>
        </Stack>

        {/* Parámetros + series + acciones */}
        <Stack direction="row" alignItems="center" spacing={1} justifyContent={{ xs: 'space-between', sm: 'flex-end' }} sx={{ flexShrink: 0 }}>
          <Stack direction="row" spacing={0.4} sx={{ color: 'gold.main' }}>
            {params.map((p) => PARAM_ICON[p] && <Box key={p} sx={{ display: 'flex' }}>{PARAM_ICON[p]}</Box>)}
          </Stack>
          {row.sets?.length > 0 && (
            <Chip size="small" label={`${row.sets.length} ${row.sets.length === 1 ? 'serie' : 'series'}`} sx={{ height: 20, bgcolor: 'surface.app', color: 'text.secondary', fontWeight: 600, fontSize: '0.62rem' }} />
          )}
          <Stack direction="row" spacing={0.75} onClick={(e) => e.stopPropagation()}>
            <Tooltip title={t('edit', { ns: 'buttons', defaultValue: 'Editar' })}>
              <IconButton size="small" onClick={() => onEdit(row)} sx={boxedActionSx('gold.main')}><EditIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title={t('delete', { ns: 'buttons', defaultValue: 'Eliminar' })}>
              <IconButton size="small" onClick={() => onDelete(row)} sx={boxedActionSx((th) => th.palette.sessionStatus.absent.fg)}><DeleteIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}

PerformanceCard.propTypes = {
  row: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onView: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

export default PerformanceCard;
